/**
 * ProductCombined3 — Custom element for 3-product combined Printful pages.
 * Manages the Toddler/Junior age group toggle with Toddler split across
 * two Printful products (e.g. 2T/3T + 4T/5T), merged into one view.
 *
 * Layout: Color → Size [Toddler|Junior toggle + size pills]
 */

const TODDLER_SIZE_MAP = {
  '2': '2 (2-3 years)',
  '3': '3 (3-4 years)',
  '4': '4 (4-5 years)',
  '5/6': '5/6 (5-6 years)',
  '2T': '2T (2-3 years)',
  '3T': '3T (3-4 years)',
  '4T': '4T (4-5 years)',
  '5T': '5T (5-6 years)',
};

const JUNIOR_SIZE_MAP = {
  XS: 'XS (4-6 years)',
  S: 'S (6-8 years)',
  M: 'M (8-10 years)',
  L: 'L (10-12 years)',
  XL: 'XL (12-14 years)',
};

class ProductCombined3 extends HTMLElement {
  constructor() {
    super();

    this.sectionId = this.dataset.section;
    this.activeGroup = 'toddler';

    // Parse variant data — two toddler sources + one junior
    this.toddlerVariants1 = this.parseJSON('[data-toddler-variants-1]');
    this.toddlerVariants2 = this.parseJSON('[data-toddler-variants-2]');
    this.juniorVariants = this.parseJSON('[data-junior-variants]');

    // Pre-formatted prices keyed by variant id, rendered server-side in the
    // visitor's market currency (e.g. €26,95 in Spain). Merged across all
    // three child products since variant IDs are globally unique. Used as
    // the only source of price strings — JS never formats money itself.
    this.priceMap = Object.assign(
      {},
      this.parseJSON('[data-toddler-prices-1]') || {},
      this.parseJSON('[data-toddler-prices-2]') || {},
      this.parseJSON('[data-junior-prices]') || {}
    );

    // If no toddler products at all, default to junior
    if (!this.toddlerVariants1 && !this.toddlerVariants2 && this.juniorVariants) {
      this.activeGroup = 'junior';
    }

    // Cache DOM references
    this.priceEls = this.querySelectorAll('[data-price]');
    this.comparePriceEl = this.querySelector('[data-compare-price]');
    this.variantIdInput = this.querySelector('[data-variant-id]');
    this.submitButton = this.querySelector('[data-add-to-cart-text]');
    this.submitBtn = this.querySelector('#ProductSubmitButton-' + this.sectionId);

    // Color panels
    this.toddlerColorPanel = this.querySelector('[data-color-panel="toddler"]');
    this.juniorColorPanel = this.querySelector('[data-color-panel="junior"]');

    // Size panels
    this.toddlerSizePanel = this.querySelector('[data-size-panel="toddler"]');
    this.juniorSizePanel = this.querySelector('[data-size-panel="junior"]');

    // Size guide modal
    this.sizeGuideModal = document.getElementById('printful-size-guide-modal');
  }

  connectedCallback() {
    this.bindAgeGroupToggle();
    this.bindVariantSelectors();
    this.bindSizeGuideModal();
    this.applySizeLabels();
    this.runInitialAvailability();
    this.updateSelectedVariant();
  }

  parseJSON(selector) {
    const el = this.querySelector(selector);
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch (e) {
      console.error('ProductCombined3: Failed to parse JSON for', selector, e);
      return null;
    }
  }

  /* ──────────────────────── Age Group Toggle ──────────────────────── */

  bindAgeGroupToggle() {
    const radios = this.querySelectorAll('[data-age-group]');
    radios.forEach((radio) => {
      radio.addEventListener('change', (e) => {
        this.activeGroup = e.target.value;
        this.showActivePanels();
        this.runInitialAvailability();
        this.updateSelectedVariant();
      });
    });
  }

  showActivePanels() {
    const isToddler = this.activeGroup === 'toddler';

    if (this.toddlerColorPanel) {
      this.toddlerColorPanel.style.display = isToddler ? '' : 'none';
      this.toddlerColorPanel.dataset.active = isToddler ? 'true' : 'false';
    }
    if (this.juniorColorPanel) {
      this.juniorColorPanel.style.display = isToddler ? 'none' : '';
      this.juniorColorPanel.dataset.active = isToddler ? 'false' : 'true';
    }

    if (this.toddlerSizePanel) {
      this.toddlerSizePanel.style.display = isToddler ? '' : 'none';
      this.toddlerSizePanel.dataset.active = isToddler ? 'true' : 'false';
    }
    if (this.juniorSizePanel) {
      this.juniorSizePanel.style.display = isToddler ? 'none' : '';
      this.juniorSizePanel.dataset.active = isToddler ? 'false' : 'true';
    }
  }

  /* ──────────────────────── Variant Selection ──────────────────────── */

  bindVariantSelectors() {
    [this.toddlerColorPanel, this.juniorColorPanel].forEach((panel) => {
      if (!panel) return;
      panel.addEventListener('change', (e) => {
        if (e.target.type === 'radio') {
          this.updateSelectedDisplay(e.target);
          this.updateAvailabilityForGroup(this.activeGroup);
          this.updateSelectedVariant();
        }
      });
    });

    [this.toddlerSizePanel, this.juniorSizePanel].forEach((panel) => {
      if (!panel) return;
      panel.addEventListener('change', (e) => {
        if (e.target.type === 'radio') {
          this.updateAvailabilityForGroup(this.activeGroup);
          this.updateSelectedVariant();
        }
      });
    });
  }

  updateSelectedDisplay(input) {
    const fieldset = input.closest('fieldset');
    const selectedDisplay = fieldset?.querySelector('[data-selected-value]');
    if (selectedDisplay) {
      selectedDisplay.textContent = input.value;
    }
  }

  /**
   * Returns merged variants for the active group.
   * For toddler: combines both toddler product variant arrays.
   */
  getActiveVariants() {
    if (this.activeGroup === 'toddler') {
      const v1 = this.toddlerVariants1 || [];
      const v2 = this.toddlerVariants2 || [];
      return [...v1, ...v2];
    }
    return this.juniorVariants;
  }

  getActiveColorPanel() {
    return this.activeGroup === 'toddler'
      ? this.toddlerColorPanel
      : this.juniorColorPanel;
  }

  getActiveSizePanel() {
    return this.activeGroup === 'toddler'
      ? this.toddlerSizePanel
      : this.juniorSizePanel;
  }

  getSelectedOptions() {
    const colorPanel = this.getActiveColorPanel();
    const sizePanel = this.getActiveSizePanel();
    const options = [];

    const colorChecked = colorPanel?.querySelector('input[type="radio"]:checked');
    if (colorChecked) options.push(colorChecked.value);

    const sizeChecked = sizePanel?.querySelector('input[type="radio"]:checked');
    if (sizeChecked) options.push(sizeChecked.value);

    return options;
  }

  findVariant(variants, selectedOptions) {
    if (!variants || !selectedOptions.length) return null;
    return variants.find((variant) => {
      return selectedOptions.every((opt, i) => {
        const key = 'option' + (i + 1);
        return variant[key] === opt;
      });
    });
  }

  updateSelectedVariant() {
    const variants = this.getActiveVariants();
    const selectedOptions = this.getSelectedOptions();
    const variant = this.findVariant(variants, selectedOptions);

    if (variant) {
      if (this.variantIdInput) {
        this.variantIdInput.value = variant.id;
        this.variantIdInput.disabled = !variant.available;
      }
      this.updatePrice(variant);
      this.updateButton(variant);
      this.updateHeroImage(variant);
    } else {
      if (this.variantIdInput) {
        this.variantIdInput.disabled = true;
      }
      this.updateButton(null);
    }
  }

  updateHeroImage(variant) {
    if (window.RBHeroImage && typeof window.RBHeroImage.swap === 'function') {
      window.RBHeroImage.swap(this, variant);
    }
  }

  updatePrice(variant) {
    if (!this.priceEls || !this.priceEls.length) return;

    const entry = this.priceMap[variant.id];
    if (!entry) return;

    this.priceEls.forEach((el) => { el.innerHTML = entry.price; });

    const priceContainer = this.querySelector('#price-' + this.sectionId);
    if (priceContainer) {
      const saleEl = priceContainer.querySelector('.price__sale');
      const regularEl = priceContainer.querySelector('.price__regular');

      if (entry.compare) {
        if (this.comparePriceEl) {
          this.comparePriceEl.innerHTML = entry.compare;
        }
        if (saleEl) saleEl.style.display = '';
        if (regularEl) regularEl.style.display = 'none';
        priceContainer.classList.add('price--on-sale');
      } else {
        if (saleEl) saleEl.style.display = 'none';
        if (regularEl) regularEl.style.display = '';
        priceContainer.classList.remove('price--on-sale');
      }
    }
  }

  updateButton(variant) {
    if (!this.submitBtn || !this.submitButton) return;

    if (!variant) {
      this.submitBtn.disabled = true;
      this.submitButton.textContent = 'Unavailable';
    } else if (!variant.available) {
      this.submitBtn.disabled = true;
      this.submitButton.textContent = 'Sold out';
    } else {
      this.submitBtn.disabled = false;
      this.submitButton.textContent = 'Add to cart';
    }
  }

  /* ──────────────────────── Availability ──────────────────────── */

  runInitialAvailability() {
    this.updateAvailabilityForGroup('toddler');
    this.updateAvailabilityForGroup('junior');
  }

  updateAvailabilityForGroup(group) {
    const variants = group === 'toddler' ? this.getToddlerMergedVariants() : this.juniorVariants;
    const colorPanel = group === 'toddler' ? this.toddlerColorPanel : this.juniorColorPanel;
    const sizePanel = group === 'toddler' ? this.toddlerSizePanel : this.juniorSizePanel;
    if (!variants || !sizePanel) return;

    const selectedColor = colorPanel?.querySelector('input[type="radio"]:checked')?.value;
    const selectedSize = sizePanel.querySelector('input[type="radio"]:checked')?.value;

    if (colorPanel) {
      colorPanel.querySelectorAll('input[type="radio"]').forEach((input) => {
        const hasAvailable = variants.some(
          (v) => v.option1 === input.value && v.option2 === selectedSize && v.available
        );
        const label = input.nextElementSibling;
        input.disabled = !hasAvailable;
        if (label) label.classList.toggle('disabled', !hasAvailable);
      });
    }

    sizePanel.querySelectorAll('input[type="radio"]').forEach((input) => {
      const hasAvailable = variants.some((v) => {
        if (!v.available) return false;
        if (colorPanel) {
          return v.option1 === selectedColor && v.option2 === input.value;
        }
        return v.option1 === input.value;
      });
      const label = input.nextElementSibling;
      input.disabled = !hasAvailable;
      if (label) label.classList.toggle('disabled', !hasAvailable);
    });
  }

  getToddlerMergedVariants() {
    const v1 = this.toddlerVariants1 || [];
    const v2 = this.toddlerVariants2 || [];
    return [...v1, ...v2];
  }

  /* ──────────────────────── Size Labels ──────────────────────── */

  applySizeLabels() {
    if (this.toddlerSizePanel) {
      this.toddlerSizePanel.querySelectorAll('[data-size-label]').forEach((el) => {
        const raw = el.dataset.sizeLabel;
        if (TODDLER_SIZE_MAP[raw]) el.textContent = TODDLER_SIZE_MAP[raw];
      });
    }

    if (this.juniorSizePanel) {
      this.juniorSizePanel.querySelectorAll('[data-size-label]').forEach((el) => {
        const raw = el.dataset.sizeLabel;
        if (JUNIOR_SIZE_MAP[raw]) el.textContent = JUNIOR_SIZE_MAP[raw];
      });
    }
  }

  /* ──────────────────────── Size Guide Modal ──────────────────────── */

  bindSizeGuideModal() {
    this.querySelectorAll('[data-modal-toggle="printful-size-guide-modal"]').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        this.openSizeGuide();
      });
    });

    if (this.sizeGuideModal) {
      this.sizeGuideModal.querySelectorAll('[data-modal-close]').forEach((btn) => {
        btn.addEventListener('click', () => this.closeSizeGuide());
      });

      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.sizeGuideModal.getAttribute('aria-hidden') === 'false') {
          this.closeSizeGuide();
        }
      });
    }
  }

  openSizeGuide() {
    if (!this.sizeGuideModal) return;
    this.sizeGuideModal.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
  }

  closeSizeGuide() {
    if (!this.sizeGuideModal) return;
    this.sizeGuideModal.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
  }
}

customElements.define('product-combined-3', ProductCombined3);
